import React, {useEffect, useState, useRef} from "react";

// Models
import {AuthUser} from "../../../Models/DTOs/AuthUser";
import {IPerson} from "../../../Models/Interfaces/IPerson";

// Components
import ButtonPill from "../../Buttons/ButtonPill";
import InputField from "../../Inputs/InputField";
import PopUp from "../../Popups/PopUp";
import ChangePassword from "../../Forms/Account/ChangePassword";
import AccountMarketingPreferences from "./AccountMarketingPreferences";
import ErrorMessage from "../../Text/ErrorMessage";
import SuccessMessage from "../../Text/SuccessMessage";

// Hooks
import {useUpdatePerson} from "../../../Hooks/User/useUpdatePerson";
import {useUpdateEmail} from "../../../Hooks/User/useUpdateEmail";

// Assets
import CheckMarkNoCircle from "../../../Assets/SVGs/Icons/YesOrNo/CheckMarkNoCircle";
import IconCancel from "../../../Assets/SVGs/Icons/YesOrNo/Cancel";
import LoadingOverlay from "../../LoadingOverlay";

type AccountBasicInfoProps = {
    userData: AuthUser | null;
    isUserDataLoading: boolean;
    getValidAccessToken: () => Promise<string | null>;
    refreshUserData: () => Promise<void>;
};

const AccountBasicInfo: React.FC<AccountBasicInfoProps> = (
    {
        userData,
        isUserDataLoading,
        getValidAccessToken,
        refreshUserData
    }
) => {
    // Local view of the person record (so we can optimistically update UI)
    const
        [person, setPerson] = useState<IPerson | null>(null),

        // First name edit state
        [newFirstName, setNewFirstName] = useState<string | undefined>(""),
        [isFirstNameEditMode, setIsFirstNameEditMode] = useState(false),
        [firstNameErrorMsg, setFirstNameErrorMsg] = useState(""),
        [firstNameSuccessMsg, setFirstNameSuccessMsg] = useState(""),

        // Email edit state
        [newEmail, setNewEmail] = useState<string | undefined>(""),
        [isEmailEditMode, setIsEmailEditMode] = useState(false),
        [emailErrorMsg, setEmailErrorMsg] = useState(""),
        [emailSuccessMsg, setEmailSuccessMsg] = useState(""),

        // PW Change popup state
        [isPWChangePopUpOpen, setIsPWChangePopUpOpen] = useState(false),

        // NEW: refs for the editable inputs
        firstNameInputRef = useRef<HTMLInputElement | null>(null),
        emailInputRef = useRef<HTMLInputElement | null>(null),

        {updatePerson} = useUpdatePerson({getValidAccessToken: () => getValidAccessToken(), userId: userData?.id}),
        {updateEmail} = useUpdateEmail({getValidAccessToken}),

        requirePerson = (): IPerson | null => {
            if (!person) {
                console.error("No person data available on user – cannot call AddOrEditPerson.");
                return null;
            }
            return person;
        },

        submitPersonUpdate = async (
            changes: Partial<IPerson>,
            onSuccess?: () => void
        ) => {
            const base = requirePerson();
            if (!base) return;

            const result = await updatePerson(base, changes);
            if (result.ok && result.updated) {
                setPerson(result.updated);
                onSuccess?.();
            }
        },

        submitEmailUpdate = async (
            onSuccess?: () => void
        ) => {
            if (newEmail) {
                const result = await updateEmail(newEmail);
                if (result.ok) {
                    onSuccess?.();
                }
            }
        },

        handleSubmitFirstName = async () => {
            await submitPersonUpdate(
                {FirstName: newFirstName},
                () => {
                    setIsFirstNameEditMode(false);
                    setFirstNameSuccessMsg("Display name successfully changed");
                    refreshUserData();
                }
            );
        },

        handleSubmitEmail = async () => {
            await submitEmailUpdate(
                () => {
                    setIsEmailEditMode(false);
                    setEmailSuccessMsg("An email will be sent to your new address confirming your change");
                    refreshUserData();
                }
            );
        };

    useEffect(() => {
        if (userData?.person) {
            setPerson(userData.person);
        }
    }, [userData]);

    useEffect(() => {
        if (isFirstNameEditMode && firstNameInputRef.current) {
            firstNameInputRef.current.focus();
            firstNameInputRef.current.select(); // optional: highlight entire text
        }
    }, [isFirstNameEditMode]);

    useEffect(() => {
        if (isEmailEditMode && emailInputRef.current) {
            emailInputRef.current.focus();
            emailInputRef.current.select();
        }
    }, [isEmailEditMode]);

    return (
        <div className="account__basic-info">
            <div className="basic-info__inner">
                <div className="basic-info__section">
                    <h3>Your Details</h3>

                    <div className="basic-info__section-wrapper">

                        {/** First Name **/}
                        <div className="account__row">
                            <p className="account__row-label">Display Name</p>

                            <div className="row__inner u-relative_hidden">
                                {
                                    isUserDataLoading ? (
                                        <LoadingOverlay size="full-cover" />
                                    ) : (
                                        <>
                                            {
                                                isFirstNameEditMode ?
                                                    <InputField
                                                        type="text"
                                                        name="firstName"
                                                        value={newFirstName ? newFirstName : ''}
                                                        label=""
                                                        onChange={event => setNewFirstName(event.target.value)}
                                                        ref={firstNameInputRef}
                                                    />
                                                    :
                                                    <p>{userData?.first_name}</p>
                                            }

                                            {
                                                isFirstNameEditMode ?
                                                    <>
                                                        <ButtonPill
                                                            className="account__yes-or-no account__yes"
                                                            icon={CheckMarkNoCircle()}
                                                            onClick={() => handleSubmitFirstName()}
                                                        />

                                                        <ButtonPill
                                                            className="account__yes-or-no account__no"
                                                            icon={IconCancel()}
                                                            onClick={() => {
                                                                setIsFirstNameEditMode(false);
                                                                setNewFirstName(userData?.first_name)
                                                            }}
                                                        />
                                                    </>
                                                    :
                                                    <ButtonPill
                                                        label="Edit"
                                                        onClick={() => {
                                                            setNewFirstName(userData?.first_name);
                                                            setIsFirstNameEditMode(true);
                                                        }}
                                                    />
                                            }


                                        </>
                                    )
                                }
                            </div>

                            {firstNameErrorMsg && <ErrorMessage errorMessage={firstNameErrorMsg} />}
                            {firstNameSuccessMsg && <SuccessMessage successMessage={firstNameSuccessMsg} />}
                        </div>

                        <div className="account__row">
                            <p className="account__row-label">Email Address</p>

                            <div className="row__inner u-relative_hidden">
                                {
                                    isUserDataLoading ? (
                                        <LoadingOverlay size="full-cover" />
                                    ) : (
                                        <>
                                            {
                                                isEmailEditMode ?
                                                    <InputField
                                                        type="text"
                                                        name="firstName"
                                                        value={newEmail ? newEmail : ""}
                                                        label=""
                                                        onChange={event => setNewEmail(event.target.value)}
                                                        ref={emailInputRef}
                                                    />
                                                    :
                                                    <p>{userData?.email}</p>
                                            }

                                            {
                                                isEmailEditMode ?
                                                    <>
                                                        <ButtonPill
                                                            className="account__yes-or-no account__yes"
                                                            icon={CheckMarkNoCircle()}
                                                            onClick={() => handleSubmitEmail()}
                                                        />

                                                        <ButtonPill
                                                            className="account__yes-or-no account__no"
                                                            icon={IconCancel()}
                                                            onClick={() => {
                                                                setIsEmailEditMode(false);
                                                                setNewEmail(userData?.email)
                                                            }}
                                                        />
                                                    </>
                                                    :
                                                    <ButtonPill
                                                        label="Edit"
                                                        onClick={() => {
                                                            setNewEmail(userData?.email);
                                                            setIsEmailEditMode(true);
                                                        }}
                                                    />
                                            }
                                        </>
                                    )
                                }
                            </div>

                            {emailErrorMsg && <ErrorMessage errorMessage={emailErrorMsg} />}
                            {emailSuccessMsg && <SuccessMessage successMessage={emailSuccessMsg} />}
                        </div>

                        {/** Password (also separate flow) **/}
                        <div className="account__row">
                            <p className="account__row-label">Password</p>

                            <div className="row__inner u-relative_hidden">
                                {
                                    isUserDataLoading ? (
                                        <LoadingOverlay size="full-cover" />
                                    ) : (
                                        <>
                                            <p>*********</p>

                                            <ButtonPill
                                                label="Edit"
                                                onClick={() => setIsPWChangePopUpOpen(true)}
                                            />
                                        </>
                                    )
                                }

                                <PopUp
                                    canBeDismissed={true}
                                    isFullScreen={true}
                                    isOpen={isPWChangePopUpOpen}
                                    onClose={() => setIsPWChangePopUpOpen(false)}
                                >
                                    <div className="account__pop-up">
                                        <ChangePassword />
                                    </div>
                                </PopUp>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="basic-info__section">
                    <h3>Marketing Preferences</h3>

                    <div className="basic-info__section-wrapper">
                        <AccountMarketingPreferences
                            userData={userData}
                            isUserDataLoading={isUserDataLoading}
                            getValidAccessToken={getValidAccessToken}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AccountBasicInfo;
